import * as React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  Stack,
  MenuItem,
  CircularProgress,
  Alert,
} from "@mui/material";
import { TableVirtuoso } from "react-virtuoso";
import { Edit, Delete, Visibility, Add } from "@mui/icons-material";
import Swal from "sweetalert2";
import axios from "axios";

const API_URL = "https://dairy.server.statify.co.ke/api/payroll/";

const columns = [
  { width: 100, label: "Code", dataKey: "code" },
  { width: 200, label: "Name", dataKey: "name" },
  { width: 150, label: "Type", dataKey: "type" },
  { width: 150, label: "Actions", dataKey: "actions" },
];

const VirtuosoTableComponents = {
  Scroller: React.forwardRef((props, ref) => (
    <TableContainer component={Paper} {...props} ref={ref} />
  )),
  Table: (props) => (
    <Table
      {...props}
      sx={{
        borderCollapse: "separate",
        tableLayout: "fixed",
      }}
    />
  ),
  TableHead: React.forwardRef((props, ref) => <TableHead {...props} ref={ref} />),
  TableRow,
  TableBody: React.forwardRef((props, ref) => <TableBody {...props} ref={ref} />),
};

export default function PayrollSetupPage() {
  const [rows, setRows] = React.useState([]);
  const [openDialog, setOpenDialog] = React.useState(false);
  const [dialogMode, setDialogMode] = React.useState("view");
  const [selected, setSelected] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [saveLoading, setSaveLoading] = React.useState(false);

  // 🔹 Enhanced fetch data with better API response handling
  const fetchData = async () => {
    try {
      setLoading(true);
      setError("");
      console.log("🔍 Fetching payroll data from:", API_URL);
      
      const response = await axios.get(API_URL, {
        timeout: 10000,
        headers: {
          'Content-Type': 'application/json',
        }
      });

      console.log("📦 Full API Response:", response);
      console.log("📊 Response data:", response.data);
      console.log("🔍 Response status:", response.status);

      let data = [];
      
      // Handle different possible response structures
      if (Array.isArray(response.data)) {
        // Case 1: Direct array
        data = response.data;
        console.log("✅ Using direct array data");
      } else if (response.data && Array.isArray(response.data.data)) {
        // Case 2: { data: [...] }
        data = response.data.data;
        console.log("✅ Using response.data.array");
      } else if (response.data && Array.isArray(response.data.payrolls)) {
        // Case 3: { payrolls: [...] }
        data = response.data.payrolls;
        console.log("✅ Using response.data.payrolls");
      } else if (response.data && Array.isArray(response.data.items)) {
        // Case 4: { items: [...] }
        data = response.data.items;
        console.log("✅ Using response.data.items");
      } else if (response.data && response.data.results && Array.isArray(response.data.results)) {
        // Case 5: { results: [...] }
        data = response.data.results;
        console.log("✅ Using response.data.results");
      } else if (response.data && typeof response.data === 'object') {
        // Case 6: Single object or other structure
        console.log("⚠️ Unexpected object structure, checking for array properties...");
        
        // Look for any array property in the response object
        const arrayProperties = Object.keys(response.data).filter(key => 
          Array.isArray(response.data[key])
        );
        
        if (arrayProperties.length > 0) {
          // Use the first array found
          data = response.data[arrayProperties[0]];
          console.log(`✅ Using array property: ${arrayProperties[0]}`);
        } else {
          // If it's a single object, wrap in array
          data = [response.data];
          console.log("✅ Wrapping single object in array");
        }
      } else {
        console.warn("❌ Unexpected response structure:", response.data);
        data = [];
      }

      console.log("🎯 Final processed data:", data);
      console.log("📝 Number of records:", data.length);
      
      // Validate data structure
      if (data.length > 0) {
        console.log("📋 Sample record structure:", data[0]);
        console.log("🔑 Available keys in first record:", Object.keys(data[0]));
      }
      
      setRows(data);
      
    } catch (error) {
      console.error("❌ Error fetching data:", error);
      let errorMessage = "Failed to fetch payroll setup data";
      
      if (error.code === 'NETWORK_ERROR' || error.message === 'Network Error') {
        errorMessage = "Network error: Cannot connect to server. Please check if the server is running at " + API_URL;
      } else if (error.response) {
        // Server responded with error status
        errorMessage = error.response.data?.message || 
                      error.response.data?.error || 
                      `Server error: ${error.response.status} - ${error.response.statusText}`;
        console.error("🚨 Server error details:", error.response.data);
      } else if (error.request) {
        // Request made but no response received
        errorMessage = "No response from server. Please check if the backend server is running.";
        console.error("🚨 No response received:", error.request);
      } else {
        errorMessage = error.message || "Unknown error occurred";
      }
      
      setError(errorMessage);
      Swal.fire("Error", errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchData();
  }, []);

  const handleOpen = (mode, row = null) => {
    setDialogMode(mode);
    setSelected(
      row || {
        code: "",
        name: "",
        type: "Earning",
      }
    );
    setOpenDialog(true);
    setError("");
  };

  const handleClose = () => {
    setOpenDialog(false);
    setSelected(null);
    setError("");
  };

  const handleSave = async () => {
    // Validation
    if (!selected.code || !selected.name || !selected.type) {
      Swal.fire("Error", "Please fill all fields.", "error");
      return;
    }

    // Code validation - ensure it's unique (only for add mode)
    if (dialogMode === "add") {
      const existingCode = rows.find(row => row.code === selected.code);
      if (existingCode) {
        Swal.fire("Error", "Code already exists. Please use a unique code.", "error");
        return;
      }
    }

    try {
      setSaveLoading(true);
      setError("");
      let response;

      const payload = {
        code: selected.code.trim(),
        name: selected.name.trim(),
        type: selected.type
      };

      console.log("💾 Saving payload:", payload);

      if (dialogMode === "add") {
        response = await axios.post(API_URL, payload, {
          headers: {
            'Content-Type': 'application/json',
          },
          timeout: 10000
        });
        console.log("✅ Add response:", response);
        Swal.fire("Success", "Payroll setup added successfully!", "success");
      } else if (dialogMode === "edit") {
        if (!selected.id) {
          Swal.fire("Error", "Invalid record ID for editing.", "error");
          return;
        }
        response = await axios.put(`${API_URL}${selected.id}`, payload, {
          headers: {
            'Content-Type': 'application/json',
          },
          timeout: 10000
        });
        console.log("✅ Edit response:", response);
        Swal.fire("Updated", "Payroll setup updated successfully!", "success");
      }
      
      handleClose();
      await fetchData(); // Refresh data
    } catch (error) {
      console.error("❌ Error saving data:", error);
      let errorMessage = "Failed to save payroll setup.";
      
      if (error.response) {
        errorMessage = error.response.data?.message || 
                      error.response.data?.error || 
                      `Server error: ${error.response.status}`;
        console.error("🚨 Save error details:", error.response.data);
      } else if (error.request) {
        errorMessage = "No response from server. Please check your connection.";
      }
      
      setError(errorMessage);
      Swal.fire("Error", errorMessage, "error");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDelete = async (id, code) => {
    if (!id) {
      Swal.fire("Error", "Invalid record ID for deletion.", "error");
      return;
    }

    const result = await Swal.fire({
      title: "Are you sure?",
      text: `You are about to delete payroll item: ${code}`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#024a59",
      cancelButtonColor: "#b00020",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel"
    });

    if (result.isConfirmed) {
      try {
        setLoading(true);
        console.log("🗑️ Deleting ID:", id);
        await axios.delete(`${API_URL}${id}`, {
          timeout: 10000
        });
        Swal.fire("Deleted!", "Payroll setup deleted successfully.", "success");
        await fetchData(); // Refresh data
      } catch (error) {
        console.error("❌ Error deleting data:", error);
        let errorMessage = "Failed to delete payroll setup.";
        
        if (error.response) {
          errorMessage = error.response.data?.message || 
                        error.response.data?.error || 
                        `Server error: ${error.response.status}`;
        } else if (error.request) {
          errorMessage = "No response from server. Please check your connection.";
        }
        
        Swal.fire("Error", errorMessage, "error");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleInputChange = (field, value) => {
    setSelected(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const fixedHeaderContent = () => (
    <TableRow>
      {columns.map((column) => (
        <TableCell
          key={column.dataKey}
          align="left"
          style={{
            width: column.width,
            backgroundColor: "#024a59",
            color: "#fff",
            fontWeight: 600,
          }}
        >
          {column.label}
        </TableCell>
      ))}
    </TableRow>
  );

  const rowContent = (_index, row) => {
    if (!row) return null;

    return (
      <React.Fragment>
        <TableCell>{row.code || "—"}</TableCell>
        <TableCell>{row.name || "—"}</TableCell>
        <TableCell>
          <Box
            sx={{
              backgroundColor:
                row.type?.toLowerCase() === "earning" ? "#024a59" : "#ffa916",
              color: "#fff",
              px: 1.5,
              py: 0.5,
              borderRadius: "10px",
              textAlign: "center",
              fontSize: 13,
              display: "inline-block",
              minWidth: 80,
            }}
          >
            {row.type || "—"}
          </Box>
        </TableCell>
        <TableCell align="center">
          <IconButton
            onClick={() => handleOpen("view", row)}
            sx={{ color: "#024a59" }}
            size="small"
            title="View"
          >
            <Visibility />
          </IconButton>
          <IconButton
            onClick={() => handleOpen("edit", row)}
            sx={{ color: "#ffa916" }}
            size="small"
            title="Edit"
          >
            <Edit />
          </IconButton>
          <IconButton
            onClick={() => handleDelete(row.id, row.code)}
            sx={{ color: "#b00020" }}
            size="small"
            title="Delete"
          >
            <Delete />
          </IconButton>
        </TableCell>
      </React.Fragment>
    );
  };

  return (
    <Box sx={{ width: "100%", mt: 3, p: 2 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Typography
          variant="h5"
          sx={{
            color: "#024a59",
            fontWeight: 700,
          }}
        >
          Payroll Setup
        </Typography>

        <Button
          startIcon={<Add />}
          onClick={() => handleOpen("add")}
          variant="contained"
          sx={{
            bgcolor: "#ffa916",
            "&:hover": { bgcolor: "#e59410" },
            color: "#fff",
            borderRadius: "10px",
            fontWeight: 600,
          }}
        >
          Add Payroll
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", mt: 10, flexDirection: "column" }}>
          <CircularProgress sx={{ color: "#024a59" }} />
          <Typography sx={{ mt: 2, color: "#024a59" }}>Loading payroll data...</Typography>
        </Box>
      ) : (
        <Paper
          sx={{
            height: 500,
            width: "100%",
            borderRadius: 3,
            overflow: "hidden",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          }}
        >
          {rows.length === 0 ? (
            <Box 
              sx={{ 
                display: "flex", 
                justifyContent: "center", 
                alignItems: "center", 
                height: "100%",
                flexDirection: "column",
                gap: 2
              }}
            >
              <Typography variant="h6" color="textSecondary">
                No payroll items found
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', maxWidth: 400 }}>
                This could mean:
                <br/>• No data exists in the database
                <br/>• API response structure is different than expected
                <br/>• There's a connection issue with the server
              </Typography>
              <Button
                startIcon={<Add />}
                onClick={() => handleOpen("add")}
                variant="contained"
                sx={{
                  bgcolor: "#ffa916",
                  "&:hover": { bgcolor: "#e59410" },
                  mt: 2
                }}
              >
                Add First Payroll Item
              </Button>
              <Button
                onClick={fetchData}
                variant="outlined"
                sx={{
                  borderColor: "#024a59",
                  color: "#024a59",
                  mt: 1
                }}
              >
                Retry Loading Data
              </Button>
            </Box>
          ) : (
            <TableVirtuoso
              data={rows}
              components={VirtuosoTableComponents}
              fixedHeaderContent={fixedHeaderContent}
              itemContent={rowContent}
            />
          )}
        </Paper>
      )}

      {/* Add/Edit/View Modal */}
      <Dialog 
        open={openDialog} 
        onClose={handleClose} 
        fullWidth 
        maxWidth="sm"
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: "#024a59",
            color: "#fff",
            fontWeight: "bold",
            fontSize: "1.2rem",
          }}
        >
          {dialogMode === "add"
            ? "Add Payroll Setup"
            : dialogMode === "edit"
            ? "Edit Payroll Setup"
            : "View Payroll Setup"}
        </DialogTitle>
        <DialogContent sx={{ mt: 2, p: 3 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {selected && (
            <Stack spacing={3}>
              <TextField
                fullWidth
                label="Code"
                value={selected.code || ""}
                disabled={dialogMode === "view"}
                onChange={(e) => handleInputChange("code", e.target.value)}
                variant="outlined"
                size="small"
                required
                error={!selected.code}
                helperText={!selected.code ? "Code is required" : ""}
              />
              <TextField
                fullWidth
                label="Name"
                value={selected.name || ""}
                disabled={dialogMode === "view"}
                onChange={(e) => handleInputChange("name", e.target.value)}
                variant="outlined"
                size="small"
                required
                error={!selected.name}
                helperText={!selected.name ? "Name is required" : ""}
              />
              <TextField
                select
                fullWidth
                label="Type"
                value={selected.type || "Earning"}
                disabled={dialogMode === "view"}
                onChange={(e) => handleInputChange("type", e.target.value)}
                variant="outlined"
                size="small"
                required
              >
                <MenuItem value="Earning">Earning</MenuItem>
                <MenuItem value="Deduction">Deduction</MenuItem>
              </TextField>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={handleClose} 
            disabled={saveLoading}
            sx={{ 
              color: "#024a59",
              fontWeight: 600,
            }}
          >
            {dialogMode === "view" ? "Close" : "Cancel"}
          </Button>
          {(dialogMode === "add" || dialogMode === "edit") && (
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={saveLoading || !selected?.code || !selected?.name}
              sx={{
                bgcolor: "#ffa916",
                "&:hover": { bgcolor: "#e59410" },
                fontWeight: 600,
                px: 3,
                minWidth: 100,
              }}
            >
              {saveLoading ? <CircularProgress size={24} /> : "Save"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}